import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { FORECAST_COLORS } from '../constants';
import {
   formatCurrency,
   formatPercent,
   pluralTitulos,
} from '../utils/format';
import {
   CardSubtitle,
   CardTitle,
   ForecastCount,
   ForecastFill,
   ForecastGrid,
   ForecastLabel,
   ForecastLegend,
   ForecastRow,
   ForecastRows,
   ForecastStack,
   ForecastTrack,
   ForecastValue,
   ForecastValueCol,
   OutcomeKey,
   OverdueCount,
   OverdueLabel,
   OverdueNote,
   OverduePanel,
   OverdueValue,
   RowTooltip,
   TooltipCount,
   TooltipGrid,
   TooltipLabel,
   TooltipValue,
} from '../styles';
import { countAmountShape } from '../utils/shapes';

// ---------------------------------------------------------------------------
// Agenda de vencimentos (endpoint `forecast`).
//
// ⚠️ Isto NÃO é previsão de caixa, e o rótulo mudou por causa disso. O número é
// o que VENCE na janela — não o que entra. Com 45,7% de adimplência em dia,
// chamar de "previsão de recebimento" superestimaria a entrada em mais de duas
// vezes. Quem quiser estimar caixa cruza esta agenda com a taxa de adimplência
// da dobra anterior; a tela não faz essa conta por ninguém.
//
// Cada horizonte vem decomposto em `total = recebido + pendente` (contrato
// revisado): `recebido` é pagamento ANTECIPADO — venceria na janela e já foi
// pago. A decomposição existe porque, com só o pendente, zero era ambíguo:
// podia ser "não há nada a vencer" ou "tudo que ia vencer já foi pago", que são
// situações opostas.
//
// ⚠️ Os horizontes são CUMULATIVOS: o de 30 dias INCLUI o de 7. Barras lado a
// lado, escaladas pelo MAIOR total — empilhar os horizontes entre si contaria o
// mesmo dinheiro três vezes.
// ---------------------------------------------------------------------------

export default function ForecastPanel({ forecast }) {
   const horizons = forecast?.horizons ?? [];
   const overdue = forecast?.vencido_acumulado;
   const [hovered, setHovered] = useState(null);

   // Escala comum a todas as linhas: o maior TOTAL. Assim o acúmulo entre
   // horizontes fica visível e as fatias são comparáveis entre linhas.
   const maxTotal = horizons.reduce(
      (max, horizon) => Math.max(max, horizon?.total?.valor ?? 0),
      0,
   );

   const overdueCount = overdue?.titulos ?? 0;
   const overdueValue = overdue?.valor ?? 0;
   const longestHorizon = horizons.length
      ? horizons.reduce(
           (longest, horizon) => Math.max(longest, horizon?.dias ?? 0),
           0,
        )
      : null;

   // O contraste é contra o que ainda VAI vencer no maior horizonte — comparar
   // com o total incluiria o que já foi pago e diluiria o problema.
   const longestPending =
      horizons.find((horizon) => horizon.dias === longestHorizon)?.pendente
         ?.valor ?? 0;
   const shareOfPending =
      longestPending > 0 && overdueValue > 0
         ? (overdueValue / longestPending) * 100
         : null;

   return (
      <>
         <div>
            <CardTitle as="h3">Agenda de vencimentos</CardTitle>
            <CardSubtitle>
               O que vence a partir de hoje, cumulativo — o horizonte de 30 dias
               inclui o de 7. É o que vence, não o que entra.
            </CardSubtitle>
         </div>

         <ForecastGrid>
            <ForecastRows>
               {horizons.map((horizon) => {
                  const total = horizon?.total?.valor ?? 0;
                  const received = horizon?.recebido?.valor ?? 0;
                  const pending = horizon?.pendente?.valor ?? 0;
                  const width = maxTotal > 0 ? (total / maxTotal) * 100 : 0;
                  const receivedShare = total > 0 ? (received / total) * 100 : 0;
                  const pendingShare = total > 0 ? (pending / total) * 100 : 0;
                  const isHovered = hovered === horizon.dias;

                  return (
                     <ForecastRow key={horizon.dias}>
                        <ForecastLabel>{horizon.dias} dias</ForecastLabel>

                        {/* O hover é da LINHA inteira, não da fatia: o segmento
                            "recebido" chega a 2,6% da barra de 30 dias e seria
                            um alvo impossível. A linha é a unidade de leitura,
                            e o tooltip traz os três números juntos. */}
                        <ForecastTrack
                           onMouseEnter={() => setHovered(horizon.dias)}
                           onMouseLeave={() => setHovered(null)}
                        >
                           <ForecastStack $width={width}>
                              {received > 0 ? (
                                 <ForecastFill
                                    style={{ flexGrow: receivedShare, flexBasis: 0 }}
                                    $share={receivedShare}
                                    $color={FORECAST_COLORS.recebido}
                                 />
                              ) : null}
                              {pending > 0 ? (
                                 <ForecastFill
                                    style={{ flexGrow: pendingShare, flexBasis: 0 }}
                                    $share={pendingShare}
                                    $color={FORECAST_COLORS.pendente}
                                 />
                              ) : null}
                           </ForecastStack>

                           {isHovered && total > 0 ? (
                              <RowTooltip>
                                 <strong>Vence em até {horizon.dias} dias</strong>
                                 <TooltipGrid>
                                    <TooltipLabel>Total</TooltipLabel>
                                    <TooltipValue>
                                       {formatCurrency(total)}
                                    </TooltipValue>
                                    <TooltipCount>
                                       {pluralTitulos(horizon?.total?.titulos ?? 0)}
                                    </TooltipCount>

                                    <TooltipLabel $color={FORECAST_COLORS.recebido}>
                                       Já pago ({formatPercent(receivedShare, 1)})
                                    </TooltipLabel>
                                    <TooltipValue>
                                       {formatCurrency(received)}
                                    </TooltipValue>
                                    <TooltipCount>
                                       {pluralTitulos(
                                          horizon?.recebido?.titulos ?? 0,
                                       )}
                                    </TooltipCount>

                                    <TooltipLabel $color={FORECAST_COLORS.pendente}>
                                       A vencer ({formatPercent(pendingShare, 1)})
                                    </TooltipLabel>
                                    <TooltipValue>
                                       {formatCurrency(pending)}
                                    </TooltipValue>
                                    <TooltipCount>
                                       {pluralTitulos(
                                          horizon?.pendente?.titulos ?? 0,
                                       )}
                                    </TooltipCount>
                                 </TooltipGrid>
                              </RowTooltip>
                           ) : null}
                        </ForecastTrack>

                        <ForecastValueCol>
                           <ForecastValue>
                              {horizon?.pendente?.titulos
                                 ? formatCurrency(pending)
                                 : '—'}
                           </ForecastValue>
                           <ForecastCount>
                              {pluralTitulos(horizon?.pendente?.titulos ?? 0)} a
                              vencer
                           </ForecastCount>
                        </ForecastValueCol>
                     </ForecastRow>
                  );
               })}

               <ForecastLegend>
                  <OutcomeKey $color={FORECAST_COLORS.recebido}>
                     Já pago (antecipado)
                  </OutcomeKey>
                  <OutcomeKey $color={FORECAST_COLORS.pendente}>
                     A vencer
                  </OutcomeKey>
               </ForecastLegend>
            </ForecastRows>

            <OverduePanel>
               <OverdueLabel>Já deveria ter entrado</OverdueLabel>
               <OverdueValue>
                  {overdueCount ? formatCurrency(overdueValue) : '—'}
               </OverdueValue>
               <OverdueCount>{pluralTitulos(overdueCount)}</OverdueCount>
               <OverdueNote>
                  {overdueCount && shareOfPending !== null && longestHorizon
                     ? `O vencido representa ${formatPercent(shareOfPending, 1)} do que ainda vai vencer em ${longestHorizon} dias.`
                     : 'Nada vencido no recorte atual — toda a carteira está a vencer.'}
               </OverdueNote>
            </OverduePanel>
         </ForecastGrid>
      </>
   );
}

ForecastPanel.propTypes = {
   forecast: PropTypes.shape({
      horizons: PropTypes.arrayOf(
         PropTypes.shape({
            dias: PropTypes.number,
            total: countAmountShape,
            recebido: countAmountShape,
            pendente: countAmountShape,
         }),
      ),
      vencido_acumulado: countAmountShape,
   }).isRequired,
};
